import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { Mail, Lock, User, Building2, ArrowLeft, UserPlus } from 'lucide-react';

export default function Register() {
  const [searchParams] = useSearchParams();
  const inviteEmail = searchParams.get('invite_email') || '';
  const planParam = searchParams.get('plan') || '';
  const [name, setName] = useState('');
  const [email, setEmail] = useState(inviteEmail);
  const [orgName, setOrgName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const isProvider = planParam === 'provider';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSubmitting(true);
    try {
      await register({ name, email, password, org_name: orgName || undefined, account_type: isProvider ? 'provider' : 'solo' });
      navigate('/welcome');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-200 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-3 transition-colors" data-testid="back-to-login">
          <ArrowLeft className="w-4 h-4" />
          Back to Sign In
        </Link>
        <Card className="shadow-lg" data-testid="register-card">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-3 p-3 bg-teal-50 rounded-full w-fit">
            <User className="w-7 h-7 text-teal-500" />
          </div>
          <CardTitle className="text-2xl font-bold">{inviteEmail ? 'Accept Invite' : isProvider ? 'Create Provider Account' : 'Create Account'}</CardTitle>
          <CardDescription>{inviteEmail ? 'Create your account to join the team' : isProvider ? 'Manage claims across multiple organizations' : 'Start managing claims for your organization'}</CardDescription>
        </CardHeader>
        <CardContent>
          {inviteEmail && (
            <div className="flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-lg px-3 py-2.5 mb-4" data-testid="invite-banner">
              <UserPlus className="w-4 h-4 text-teal-600 shrink-0" />
              <p className="text-xs text-teal-700">You've been invited! Register with <strong>{inviteEmail}</strong> to join.</p>
            </div>
          )}
          {isProvider && !inviteEmail && (
            <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5 mb-4" data-testid="provider-plan-banner">
              <Building2 className="w-4 h-4 text-blue-600 shrink-0" />
              <p className="text-xs text-blue-700"><strong>Provider Plan</strong> — $99/mo base. 3 orgs, 6 companies. 30-day free trial.</p>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="reg-name">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <Input id="reg-name" placeholder="John Doe" value={name} onChange={(e) => setName(e.target.value)} className="pl-9" required data-testid="register-name-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reg-email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <Input id="reg-email" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-9" required readOnly={!!inviteEmail} data-testid="register-email-input" />
              </div>
            </div>
            {!inviteEmail && (
            <div className="space-y-1.5">
              <Label htmlFor="reg-org">Organization Name <span className="text-gray-400 text-xs">(optional)</span></Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <Input id="reg-org" placeholder="Acme Corp" value={orgName} onChange={(e) => setOrgName(e.target.value)} className="pl-9" data-testid="register-org-input" />
              </div>
            </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="reg-password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <Input id="reg-password" type="password" placeholder="Min 6 characters" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-9" required data-testid="register-password-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reg-confirm">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <Input id="reg-confirm" type="password" placeholder="Repeat password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="pl-9" required data-testid="register-confirm-input" />
              </div>
            </div>
            <Button type="submit" className="w-full bg-teal-500 hover:bg-teal-600" disabled={submitting} data-testid="register-submit-btn">
              {submitting ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-white px-2 text-gray-500">Or continue with</span>
            </div>
          </div>

          <Button variant="outline" className="w-full" onClick={loginWithGoogle} data-testid="google-register-btn">
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Sign up with Google
          </Button>

          <p className="text-center mt-4 text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-500 hover:underline font-medium" data-testid="login-link">Sign in</Link>
          </p>
        </CardContent>
        </Card>
      </div>
    </div>
  );
}
